import React, { useEffect } from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from "@material-ui/core/styles";
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Snackbar from '@material-ui/core/Snackbar';
import CloseIcon from '@material-ui/icons/Close';

const useStyles = makeStyles(theme => ({
    paper: {
        padding: theme.spacing(3),
    },
    dialogTitle: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText
    },
    dialogButton: {
        minWidth: 170,
        maxWidth: '100%'
    },
    dialogContent: {
        minWidth: 500,
        maxWidth: '100%',
        marginBottom: 10,
    },
    cancelButton: {
        minWidth: 100,
    }
}));


const WeddingPlanners = () => {
    const classes = useStyles();

    const [openSnackbar, setOpenSnackbar] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackbar(false);
    };

    const [data, setData] = React.useState({
        isLoading: true
    });
    const [showDialog, setShowDialog] = React.useState(false);
    const defaultFormData = {
        email: '',
    };
    const [formData, setFormData] = React.useState(defaultFormData);
    const [formErrors, setFormErrors] = React.useState('');

    const handleClose = () => {
        setShowDialog(false);
        setFormData(defaultFormData);
        setFormErrors('');
    }

    const updateFormState = (value, name) => {
        const data = {
            ...formData,
            [name]: value,
        };
        setFormData(data);
    }
    const handleChange = name => event => {
        const value = event.target.value;
        updateFormState(value, name);
    }
    const handleSubmit = () => {
        const data = {
            ...formData,
            _token: window._token
        };
        const url = '/api/admin/wedding-planners';
        fetch(url, {
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
                'Content-Type': 'application/json'
            }
        })
            .then(response => {
                return response.json();
            })
            .then(result => {
                if (result.errors) {
                    setFormErrors(result.errors);
                    return;
                }
                setTimeout(() => {
                    if (result.created) {
                        setSnackbarMessage('Successfully added wedding planner');
                    } else {
                        setSnackbarMessage('Made existing user a wedding planner');
                    }
                    handleClose();
                    setOpenSnackbar(true);
                    loadWeddingPlanners();
                }, 30);
            })
            .catch(reason => {
                alert('Please try again');
            });
    }

    const loadWeddingPlanners = () => {
        let url = '/api/admin/wedding-planners';
        fetch(url)
            .then(response => {
                return response.json();
            })
            .then(result => {
                if (!result.errors) {
                    setData({ isLoading: false, users: result });
                } else {
                    setData({ isLoading: false, errors: result.errors })
                }
            })
            .catch(reason => {
                console.log(reason);
                alert('An error prevented retrieving wedding planners. Please refresh the page and try again.');
            })
    }

    useEffect(() => {
        document.title = 'Wedding Planners - Enduring Promises';
        loadWeddingPlanners();
    }, []);

    return (
        <Paper square className={classes.paper}>
            <Typography variant="h6">Wedding Planners</Typography>
            <Box mt={2} mb={4}>
                <Button color="primary" variant="contained" onClick={() => setShowDialog(true)}>Add Wedding Planner</Button>
            </Box>
            {data.isLoading && (
                <CircularProgress />
            )}
            {data.users && data.users.length > 0 && (
                <table className="table table-striped table-bordered">
                    <thead className="thead-dark">
                        <tr>
                            <th>Email Address</th>
                            <th>Name</th>
                            <th>Created</th>
                            <th>Is Active</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.users.map(row => {
                            return (
                                <tr key={`user-${row.id}`}>
                                    <td>{row.email}</td>
                                    <td>{row.name}</td>
                                    <td>{row.created_at}</td>
                                    <td>{row.is_active ? 'Yes' : 'No'}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            )}
            <Dialog
                open={showDialog}
                onClose={handleClose}
                maxWidth='xl'
                aria-labelledby="form-dialog-title"
            >
                <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>Add Wedding Planner</DialogTitle>
                <DialogContent dividers className={classes.dialogContent}>
                    <Grid container spacing={2}>
                        <Grid item xs={10}>
                            <TextField
                                autoFocus
                                fullWidth
                                id="email"
                                margin="normal"
                                onChange={handleChange('email')}
                                label="Email address"
                                type="email"
                                value={formData.email || ''}
                                required
                            />
                            {formErrors && (
                                <React.Fragment>
                                    <p className="mt-2 mb-1">Please fix the following errors:</p>
                                    <ul>
                                        {formErrors.map(err => (
                                            <li key={err[0]}>{err[1]}</li>
                                        ))}
                                    </ul>
                                </React.Fragment>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button onClick={handleClose} variant="outlined" className={[classes.dialogButton, classes.cancelButton].join(', ')}>
                        Cancel
                    </Button>
                    <Box component="span">
                        <Button
                            disabled={!formData.email}
                            variant="contained"
                            color="primary"
                            type="submit"
                            className={classes.dialogButton}
                            onClick={handleSubmit}
                        >
                            Add Wedding Planner
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                message={snackbarMessage}
                action={
                    <React.Fragment>
                        <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnackbar}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </React.Fragment>
                }
            />
        </Paper>
    )
}

export default WeddingPlanners;
